import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 seconds timeout
});

// Add request interceptor for debugging
api.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Add response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.message);
    return Promise.reject(error);
  }
);

/**
 * Run a backtest
 * @param {Object} backtestData - Backtest parameters
 * @param {boolean} useFastEngine - Use fast vectorized engine (default: true)
 * @returns {Promise} Backtest results
 */
export const runBacktest = async (backtestData, useFastEngine = true) => {
  try {
    const response = await api.post(`/backtest?fast=${useFastEngine}`, backtestData);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || error.message || 'Backtest failed');
  }
};

/**
 * Get backtest result by ID
 * @param {number} backtestId - Backtest ID
 * @returns {Promise} Backtest results
 */
export const getBacktestResult = async (backtestId) => {
  try {
    const response = await api.get(`/backtest/${backtestId}`);
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch backtest result');
  }
};

/**
 * Get list of available symbols
 * @returns {Promise<Array>} Array of symbol strings
 */
export const getSymbols = async () => {
  try {
    const response = await api.get('/symbols');
    return response.data;
  } catch (error) {
    console.error('Failed to fetch symbols:', error);
    return [];
  }
};

/**
 * Get stock data for a symbol
 * @param {string} symbol - Stock symbol
 * @param {string} timeframe - Timeframe (1min, 5min, 1day, etc.)
 * @param {string} startDate - Start date (YYYY-MM-DD)
 * @param {string} endDate - End date (YYYY-MM-DD)
 * @returns {Promise} Stock data
 */
export const getStockData = async (symbol, timeframe, startDate, endDate) => {
  try {
    const response = await api.get(`/data/${symbol}`, {
      params: { 
        timeframe, 
        start_date: startDate, 
        end_date: endDate 
      }
    });
    return response.data;
  } catch (error) {
    throw new Error(error.response?.data?.detail || 'Failed to fetch stock data');
  }
};

/**
 * Check API health
 * @returns {Promise} Health status
 */
export const checkHealth = async () => {
  try {
    const response = await axios.get(`${API_BASE_URL.replace('/api', '')}/health`);
    return response.data;
  } catch (error) {
    throw new Error('API is not responding');
  }
};

export default api;